// B10 (mockup docs/renders/trip-book tb01): the family's trips as books on the Crate shelf. Each
// trip is a spine with its name, its year and a season mark; the trip under way is marked NOW, and
// an empty slot with a "?" waits for the next trip. Pure, so the 3D shelf and the flat (Calm) shelf
// show the same books in the same order.

export interface ShelfTrip {
  id: string
  title: string
  app?: { startDate: string; endDate: string } | undefined
}

export interface ShelfBook {
  id: string
  title: string
  /** Short year on the spine ("’27"), or '' when the trip has no dates. */
  year: string
  colour: string
  /** A season mark from the trip's start month: ❄ winter, ✿ spring, ☀ summer, ✦ autumn. */
  mark: string
  /** The trip under way. */
  now: boolean
}

/** Spine colours (the app's palette), picked from the trip id so a book keeps its colour. */
export const SPINES = ['#2F7BFF', '#36D399', '#FF6FB5', '#9B6BFF', '#FF8A3D', '#43C6DB', '#E0A21A', '#FF4F4F'] as const
/** How many books stand on the shelf (two tiers of four, one slot kept for the next trip). */
export const SHELF_BOOKS = 7

function hash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

export function seasonMark(date: string | undefined): string {
  const m = date ? Number(date.slice(5, 7)) : 0
  if (!m) return '✦'
  if (m === 12 || m <= 2) return '❄'
  if (m <= 5) return '✿'
  if (m <= 8) return '☀'
  return '✦'
}

/**
 * The books, newest first: the trip under way, then the rest by start date (undated last).
 * Neighbouring books never share a colour.
 */
export function shelfBooks(trips: readonly ShelfTrip[], activeId: string | null | undefined): ShelfBook[] {
  const start = (x: ShelfTrip) => x.app?.startDate ?? ''
  const sorted = [...trips].sort((a, b) => {
    if (a.id === activeId) return -1
    if (b.id === activeId) return 1
    return start(b).localeCompare(start(a)) || a.title.localeCompare(b.title)
  })
  const books: ShelfBook[] = []
  for (const x of sorted.slice(0, SHELF_BOOKS)) {
    let c = hash(x.id) % SPINES.length
    if (books.length && books[books.length - 1]!.colour === SPINES[c]) c = (c + 1) % SPINES.length
    const s = start(x)
    books.push({ id: x.id, title: x.title, year: s ? `’${s.slice(2, 4)}` : '', colour: SPINES[c]!, mark: seasonMark(s || undefined), now: x.id === activeId })
  }
  return books
}
